import * as React from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import CollectionsIcon from '@mui/icons-material/Collections';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import { API_URL } from '../api';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Link, useLocation } from 'react-router-dom';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import ChildCareRoundedIcon from '@mui/icons-material/ChildCareRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';

// Collapsible "Users" group with role-filtered children. Auto-expands when the
// current route is one of the children.
const USER_PATHS = ['/parents', '/students', '/teacher-accounts', '/user', '/waiting-list', '/emails', '/email-templates', '/emails-sent', '/accounts/pending'];
function UsersNavGroup() {
  const location = useLocation();
  const active = USER_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );
  const [open, setOpen] = React.useState(active);
  React.useEffect(() => { if (active) setOpen(true); }, [active]);

  return (
    <>
      <ListItemButton onClick={() => setOpen((o) => !o)}>
        <ListItemIcon>
          <SupervisorAccountRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Users" />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <PendingApprovalsNavItem nested />
        <Link to="/parents">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><SupervisorAccountRoundedIcon /></ListItemIcon>
            <ListItemText primary="Parents" />
          </ListItemButton>
        </Link>
        <Link to="/students">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><ChildCareRoundedIcon /></ListItemIcon>
            <ListItemText primary="Students" />
          </ListItemButton>
        </Link>
        <Link to="/teacher-accounts">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><SchoolRoundedIcon /></ListItemIcon>
            <ListItemText primary="Teachers" />
          </ListItemButton>
        </Link>
        <Link to="/user">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><AdminPanelSettingsRoundedIcon /></ListItemIcon>
            <ListItemText primary="Admin users" />
          </ListItemButton>
        </Link>
      </Collapse>
    </>
  );
}

/**
 * Pending-approvals sidebar item. Polls /api/admin/accounts/pending?countOnly=1
 * every 60s to show the count badge so admin sees new signups appearing.
 * Mirrors WaitingListNavItem / InboxNavItem.
 */
function PendingApprovalsNavItem({ nested = false } = {}) {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/accounts/pending?countOnly=1`,
          { headers: { Authorization: cookies.get('TOKEN') } }
        );
        if (!cancelled) setCount(data?.total || 0);
      } catch {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return (
    <Link to="/accounts/pending">
      <ListItemButton sx={nested ? { pl: 4 } : undefined}>
        <ListItemIcon>
          <PersonAddRoundedIcon />
        </ListItemIcon>
        <ListItemText
          primary="Pending approvals"
          primaryTypographyProps={{ noWrap: true }}
          sx={{ pr: count > 0 ? 1 : 0 }}
        />
        {count > 0 && (
          <Chip
            label={count}
            color="error"
            size="small"
            sx={{
              height: 22,
              minWidth: 22,
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}
      </ListItemButton>
    </Link>
  );
}

/**
 * Cover requests sidebar item. Polls the admin cover-requests list with
 * `?status=open&countOnly=1` every 60s and shows a red badge whenever
 * there's at least one open request. Mirrors PendingApprovalsNavItem.
 */
function CoverRequestsNavItem({ nested = false } = {}) {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/cover-requests?status=open&countOnly=1`,
          { headers: { Authorization: cookies.get('TOKEN') } }
        );
        if (!cancelled) setCount(data?.total || 0);
      } catch {}
    };
    load();
    const id = setInterval(load, 60000);
    // Listen for a same-tab event dispatched from the Cover requests page
    // after admin approves / rejects / assigns so the badge updates
    // immediately rather than waiting up to 60s.
    const onChanged = () => load();
    window.addEventListener('cover-requests:changed', onChanged);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('cover-requests:changed', onChanged);
    };
  }, []);
  return (
    <Link to="/cover-requests">
      <ListItemButton sx={nested ? { pl: 4 } : undefined}>
        <ListItemIcon>
          <HandshakeRoundedIcon />
        </ListItemIcon>
        <ListItemText
          primary="Cover requests"
          primaryTypographyProps={{ noWrap: true }}
          sx={{ pr: count > 0 ? 1 : 0 }}
        />
        {count > 0 && (
          <Chip
            label={count}
            color="error"
            size="small"
            sx={{
              height: 22,
              minWidth: 22,
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}
      </ListItemButton>
    </Link>
  );
}

import ImportContactsRoundedIcon from '@mui/icons-material/ImportContactsRounded';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import LocalLibraryRoundedIcon from '@mui/icons-material/LocalLibraryRounded';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import QuizIcon from '@mui/icons-material/Quiz';
import HomeIcon from '@mui/icons-material/Home';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import PaymentsTwoToneIcon from '@mui/icons-material/PaymentsTwoTone';
import { Menu, MenuItem } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';

// Collapsible "Teaching" group — Term dates, Tutoring classes, Optional topics.
const TEACHING_PATHS = ['/tutoring-classes', '/academic-years', '/optional-topics'];
function TeachingNavGroup() {
  const location = useLocation();
  const active = TEACHING_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );
  const [open, setOpen] = React.useState(active);
  React.useEffect(() => { if (active) setOpen(true); }, [active]);

  return (
    <>
      <ListItemButton onClick={() => setOpen((o) => !o)}>
        <ListItemIcon>
          <SchoolRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Teaching" />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Link to="/academic-years">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><CalendarMonthRoundedIcon /></ListItemIcon>
            <ListItemText primary="Term dates" />
          </ListItemButton>
        </Link>
        <Link to="/tutoring-classes">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><SchoolRoundedIcon /></ListItemIcon>
            <ListItemText primary="Tutoring classes" />
          </ListItemButton>
        </Link>
        <Link to="/optional-topics">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><ImportContactsRoundedIcon /></ListItemIcon>
            <ListItemText primary="Optional topics" />
          </ListItemButton>
        </Link>
        <Link to="/registers">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><CalendarMonthRoundedIcon /></ListItemIcon>
            <ListItemText primary="Class register" />
          </ListItemButton>
        </Link>
        <Link to="/reports">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><ImportContactsRoundedIcon /></ListItemIcon>
            <ListItemText primary="Progress reports" />
          </ListItemButton>
        </Link>
        <Link to="/tutor-payouts">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><PaymentsTwoToneIcon /></ListItemIcon>
            <ListItemText primary="Tutor payouts" />
          </ListItemButton>
        </Link>
        <CoverRequestsNavItem nested />
        {/* Cover requests moved into a badge-polling component above. */}
        <Link to="/renewals">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><QuizIcon /></ListItemIcon>
            <ListItemText primary="Renewals" />
          </ListItemButton>
        </Link>
      </Collapse>
    </>
  );
}

// Collapsible "Emails" group with composer / templates / sent emails children.
// Auto-expands when the current route is one of the children.
const EMAIL_PATHS = ['/emails', '/email-templates', '/emails-sent'];
const INVOICE_PATHS = ['/invoices', '/invoice-descriptions'];
const SHOP_PATHS = ['/products', '/orders'];
const CATALOG_PATHS = ['/subjects', '/levels'];

function ShopNavGroup() {
  const location = useLocation();
  const active = SHOP_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );
  const [open, setOpen] = React.useState(active);
  React.useEffect(() => { if (active) setOpen(true); }, [active]);

  return (
    <>
      <ListItemButton onClick={() => setOpen((o) => !o)}>
        <ListItemIcon>
          <StorefrontIcon />
        </ListItemIcon>
        <ListItemText primary="Shop" />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Link to="/products">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><StorefrontIcon /></ListItemIcon>
            <ListItemText primary="Products" />
          </ListItemButton>
        </Link>
        <Link to="/orders">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><ShoppingCartIcon /></ListItemIcon>
            <ListItemText primary="Orders" />
          </ListItemButton>
        </Link>
      </Collapse>
    </>
  );
}

function CatalogNavGroup() {
  const location = useLocation();
  const active = CATALOG_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );
  const [open, setOpen] = React.useState(active);
  React.useEffect(() => { if (active) setOpen(true); }, [active]);

  return (
    <>
      <ListItemButton onClick={() => setOpen((o) => !o)}>
        <ListItemIcon>
          <ImportContactsRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Subjects & levels" />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Link to="/subjects">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><ImportContactsRoundedIcon /></ListItemIcon>
            <ListItemText primary="Subjects" />
          </ListItemButton>
        </Link>
        <Link to="/levels">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><ImportContactsRoundedIcon /></ListItemIcon>
            <ListItemText primary="Levels" />
          </ListItemButton>
        </Link>
      </Collapse>
    </>
  );
}

function InvoicesNavGroup() {
  const location = useLocation();
  const active = INVOICE_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );
  const [open, setOpen] = React.useState(active);
  React.useEffect(() => { if (active) setOpen(true); }, [active]);

  return (
    <>
      <ListItemButton onClick={() => setOpen((o) => !o)}>
        <ListItemIcon>
          <ReceiptLongIcon />
        </ListItemIcon>
        <ListItemText primary="Invoices" />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Link to="/invoices">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><ReceiptLongIcon /></ListItemIcon>
            <ListItemText primary="Invoices" />
          </ListItemButton>
        </Link>
        <Link to="/invoice-descriptions">
          <ListItemButton sx={{ pl: 4 }}>
            <ListItemIcon><ReceiptLongIcon /></ListItemIcon>
            <ListItemText primary="Invoice descriptions" />
          </ListItemButton>
        </Link>
      </Collapse>
    </>
  );
}
function EmailsNavGroup({ nested = false } = {}) {
  const location = useLocation();
  const active = EMAIL_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );
  const [open, setOpen] = React.useState(active);
  React.useEffect(() => { if (active) setOpen(true); }, [active]);

  // When nested inside another collapse, the header sits at pl: 4 (one
  // level in) and the children at pl: 7 (two levels in) so the hierarchy
  // reads visually.
  const headerSx = nested ? { pl: 4 } : undefined;
  const childSx = nested ? { pl: 7 } : { pl: 4 };

  return (
    <>
      <ListItemButton onClick={() => setOpen((o) => !o)} sx={headerSx}>
        <ListItemIcon>
          <AlternateEmailRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Emails" />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Link to="/emails">
          <ListItemButton sx={childSx}>
            <ListItemIcon><AlternateEmailRoundedIcon /></ListItemIcon>
            <ListItemText primary="Email composer" />
          </ListItemButton>
        </Link>
        <Link to="/email-templates">
          <ListItemButton sx={childSx}>
            <ListItemIcon><AlternateEmailRoundedIcon /></ListItemIcon>
            <ListItemText primary="Email templates" />
          </ListItemButton>
        </Link>
        <Link to="/emails-sent">
          <ListItemButton sx={childSx}>
            <ListItemIcon><AlternateEmailRoundedIcon /></ListItemIcon>
            <ListItemText primary="Sent emails" />
          </ListItemButton>
        </Link>
      </Collapse>
    </>
  );
}

// Lightweight polling badge for the Contact Enquiries nav row. Fetches the
// unread count every 60s; renders a small red dot with the number.
const cookies = new Cookies();

function InboxNavItem() {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/messaging/unread-count`,
          { headers: { Authorization: cookies.get('TOKEN') } }
        );
        if (!cancelled) setCount(data?.count || 0);
      } catch {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return (
    <Link to="/inbox">
      <ListItemButton>
        <ListItemIcon>
          <ChatRoundedIcon />
        </ListItemIcon>
        <ListItemText
          primary="Messages"
          primaryTypographyProps={{ noWrap: true }}
          sx={{ pr: count > 0 ? 1 : 0 }}
        />
        {count > 0 && (
          <Chip
            label={count}
            color="error"
            size="small"
            sx={{
              height: 22,
              minWidth: 22,
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}
      </ListItemButton>
    </Link>
  );
}

function WaitingListNavItem({ nested = false } = {}) {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/waiting-list/unread-count`,
          { headers: { Authorization: cookies.get('TOKEN') } }
        );
        if (!cancelled) setCount(data?.count || 0);
      } catch {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return (
    <Link to="/waiting-list">
      <ListItemButton sx={nested ? { pl: 4 } : undefined}>
        <ListItemIcon>
          <ImportContactsRoundedIcon />
        </ListItemIcon>
        <ListItemText
          primary="Waiting list"
          primaryTypographyProps={{ noWrap: true }}
          sx={{ pr: count > 0 ? 1 : 0 }}
        />
        {count > 0 && (
          <Chip
            label={count}
            color="error"
            size="small"
            sx={{
              height: 22,
              minWidth: 22,
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}
      </ListItemButton>
    </Link>
  );
}

function ContactEnquiriesNavItem() {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/contact-enquiries/unread-count`,
          { headers: { Authorization: cookies.get('TOKEN') } }
        );
        if (!cancelled) setCount(data?.count || 0);
      } catch {
        // Silent — sidebar shouldn't error-noise on a polled fetch.
      }
    };
    load();
    const id = setInterval(load, 60000);
    // Detail/list pages dispatch this event after mark-read / mark-unread /
    // reply / delete actions so the badge stays in sync without a page reload.
    const onChanged = () => load();
    window.addEventListener('contact-enquiries:unread-changed', onChanged);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('contact-enquiries:unread-changed', onChanged);
    };
  }, []);

  return (
    <Link to="/contact-enquiries">
      <ListItemButton>
        <ListItemIcon>
          <AlternateEmailRoundedIcon />
        </ListItemIcon>
        <ListItemText
          primary="Contact Enquiries"
          primaryTypographyProps={{ noWrap: true }}
          sx={{ pr: count > 0 ? 1 : 0 }}
        />
        {count > 0 && (
          <Chip
            label={count}
            color="error"
            size="small"
            sx={{
              height: 22,
              minWidth: 22,
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}
      </ListItemButton>
    </Link>
  );
}

export const mainListItems = (
  <>
    {/* Top-priority items per Meera's spec. Waiting list is a peer of
        Contact Enquiries — both are inbound queues admin actions from. */}
    <InboxNavItem />
    <ContactEnquiriesNavItem />
    <WaitingListNavItem />
    <Link to="/consultations">
      <ListItemButton>
        <ListItemIcon>
          <AccessTimeIcon />
        </ListItemIcon>
        <ListItemText primary="Consultations" />
      </ListItemButton>
    </Link>
    <EmailsNavGroup />
    <InvoicesNavGroup />
    <TeachingNavGroup />
    <UsersNavGroup />

    <ShopNavGroup />
    <Link to="/class">
      <ListItemButton>
        <ListItemIcon>
          <ImportContactsRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Classes" />
      </ListItemButton>
    </Link>
    <Link to="/gallery">
      <ListItemButton>
        <ListItemIcon>
          <CollectionsIcon />
        </ListItemIcon>
        <ListItemText primary="Gallery" />
      </ListItemButton>
    </Link>
    <Link to="/course">
      <ListItemButton>
        <ListItemIcon>
          <SchoolRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Courses" />
      </ListItemButton>
    </Link>
    <Link to="/course-orders">
      <ListItemButton>
        <ListItemIcon>
          <ReceiptLongIcon />
        </ListItemIcon>
        <ListItemText primary="Course Orders" />
      </ListItemButton>
    </Link>
    <Link to="/timetable">
      <ListItemButton>
        <ListItemIcon>
          <AccessTimeIcon />
        </ListItemIcon>
        <ListItemText primary="Timetable" />
      </ListItemButton>
    </Link>
    {/* <Link to="/payment-history">
      <ListItemButton>
        <ListItemIcon>
          <PaymentIcon />
        </ListItemIcon>
        <ListItemText primary="Stripe Payment" />
      </ListItemButton>
    </Link> */}
    <Link to="/resource">
      <ListItemButton>
        <ListItemIcon>
          <LocalLibraryRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Resources" />
      </ListItemButton>
    </Link>
    <Link to="/testimonial">
      <ListItemButton>
        <ListItemIcon>
          <HandshakeRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Testimonials" />
      </ListItemButton>
    </Link>
    <Link to="/about-us">
      <ListItemButton>
        <ListItemIcon>
          <HomeIcon />
        </ListItemIcon>
        <ListItemText primary="About Us" />
      </ListItemButton>
    </Link>

    <Link to="/FAQ">
      <ListItemButton>
        <ListItemIcon>
          <QuizIcon />
        </ListItemIcon>
        <ListItemText primary="FAQ" />
      </ListItemButton>
    </Link>

    <Link to="/teacher">
      <ListItemButton>
        <ListItemIcon>
          <Groups2RoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Meet the Team" />
      </ListItemButton>
    </Link>
    {/* <Link to="/contact">
      <ListItemButton>
        <ListItemIcon>
          <AlternateEmailRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Contact Us OLD" />
      </ListItemButton>
    </Link> */}
    <Link to="/childprofile">
      <ListItemButton>
        <ListItemIcon>
          {/* <ForumIcon /> */}
          <AlternateEmailRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Contact Us (legacy)" />
      </ListItemButton>
    </Link>
    <Link to="/join-team">
      <ListItemButton>
        <ListItemIcon>
          {/* <ForumIcon /> */}
          <AlternateEmailRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Join the Team" />
      </ListItemButton>
    </Link>
    <Link to="/subscriber">
      <ListItemButton>
        <ListItemIcon>
          {/* <ForumIcon /> */}
          <PaymentsTwoToneIcon />
        </ListItemIcon>
        <ListItemText primary="Payments" />
      </ListItemButton>
    </Link>
    <CatalogNavGroup />
    <Link to="/settings">
      <ListItemButton>
        <ListItemIcon>
          <MiscellaneousServicesIcon />
        </ListItemIcon>
        <ListItemText primary="Settings" />
      </ListItemButton>
    </Link>
  </>
);
